/**
 * SOVRANOVA — Settings Panel
 * Slide-in panel for voice/text preferences. Opened from the header gear icon.
 */
import { useState, useEffect } from 'react'
import { useSettings } from '../../hooks/useSettings'
import { api, supabase } from '../../services/api'
import { isDisguiseModeActive } from '../../hooks/useDisguiseMode'
import { subscribeToPush, unsubscribeFromPush } from '../../services/push'
import { VoicePicker } from './VoicePicker'
import { ConversationVoicePicker } from './ConversationVoicePicker'

// Raw Hume Voice-Library picker — OFF unless explicitly enabled at build time.
const FEATURE_HUME_VOICE_PICKER = import.meta.env.VITE_FEATURE_HUME_VOICE_PICKER === 'true'
import { RegionPicker } from './RegionPicker'

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      style={{
        width: 46, height: 26, borderRadius: 13, border: 'none',
        background: disabled ? 'var(--og-n200, #eee7db)' : on ? 'var(--og-accent2, #7a8a5e)' : 'var(--og-n300, #dcd3c4)',
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background .2s', flexShrink: 0, opacity: disabled ? 0.5 : 1,
      }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20,
        borderRadius: '50%', background: 'white', transition: 'left .2s',
      }} />
    </button>
  )
}

function Row({ title, desc, on, onChange, disabled }) {
  return (
    <div style={s.row}>
      <div style={{ flex: 1 }}>
        <p style={s.rowTitle}>{title}</p>
        <p style={s.rowDesc}>{desc}</p>
      </div>
      <Toggle on={on} onChange={onChange} disabled={disabled} />
    </div>
  )
}

// "Share your story" (David, 28 Aug 2026): let people give SOVRANOVA history
// from outside — a transcript from another AI, notes, their own written story.
// The backend distills it into the same long-term memory the sessions use, so
// the very next conversation already knows it. Paste or upload .txt/.md.
function ShareYourStory() {
  const [text, setText]       = useState('')
  const [busy, setBusy]       = useState(false)
  const [learned, setLearned] = useState(null)
  const [err, setErr]         = useState(null)

  const handleFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 400 * 1024) { setErr('That file is too large — please keep it under 400KB of text.'); return }
    const reader = new FileReader()
    reader.onload = () => { setText(String(reader.result || '')); setErr(null) }
    reader.onerror = () => setErr('Could not read that file. Try pasting the text instead.')
    reader.readAsText(f)
    e.target.value = ''
  }

  const handleImport = async () => {
    if (busy || !text.trim()) return
    setBusy(true); setErr(null); setLearned(null)
    try {
      const r = await api.post('/memory/import', { text: text.trim() })
      if (r?.ok) { setLearned(r.learned); setText('') }
      else setErr('That didn’t go through — please try again.')
    } catch (e) {
      setErr('That didn’t go through — please try again in a moment.')
    }
    setBusy(false)
  }

  return (
    <div style={{ marginTop:'1.5rem' }}>
      <p style={s.sectionLabel}>SHARE YOUR STORY</p>
      <p style={s.rowDesc}>
        Give SOVRANOVA more of your history — paste a conversation you&rsquo;ve had elsewhere,
        notes, or anything you&rsquo;d like it to remember about you. It becomes part of your
        memory here and your companion will know it from your very next session.
      </p>
      {learned ? (
        <div style={sy.learnedBox}>
          <p style={sy.learnedText}>{learned}</p>
          <button style={sy.again} onClick={() => setLearned(null)}>Share something else</button>
        </div>
      ) : (
        <>
          <textarea
            style={sy.area} rows={5} value={text} disabled={busy}
            placeholder="Paste your story, a transcript, or anything you want me to remember…"
            onChange={e => { setText(e.target.value); setErr(null) }}
          />
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginTop:'0.5rem' }}>
            <label style={sy.fileBtn}>
              Upload .txt
              <input type="file" accept=".txt,.md,text/plain,text/markdown" style={{ display:'none' }} onChange={handleFile} disabled={busy} />
            </label>
            <button style={{ ...sy.importBtn, opacity: (!text.trim() || busy) ? 0.5 : 1 }} onClick={handleImport} disabled={busy || !text.trim()}>
              {busy ? 'Weaving it into memory…' : 'Add to my memory'}
            </button>
          </div>
          {err && <p style={{ ...s.rowDesc, color:'#a03020', marginTop:'0.4rem' }}>{err}</p>}
        </>
      )}
    </div>
  )
}

// "What SOVRANOVA remembers" (Memory v2, 3 Sep 2026): the person can SEE each
// memory with where it came from, say "that's not right" (the original is kept
// as history and the correction becomes current), FORGET one, or download all
// of it. Individual memories only — couples material lives inside Couples.
function WhatIRemember() {
  const [open, setOpen]         = useState(false)
  const [items, setItems]       = useState(null)
  const [err, setErr]           = useState(null)
  const [editing, setEditing]   = useState(null)   // record id being corrected
  const [draft, setDraft]       = useState('')
  const [busyId, setBusyId]     = useState(null)

  const load = async () => {
    setErr(null)
    try { const r = await api.get('/memory/records'); setItems(r?.records || []) }
    catch (_) { setErr('Could not load your memory right now.'); setItems([]) }
  }
  const toggle = () => { const next = !open; setOpen(next); if (next && items === null) load() }

  const forget = async (id) => {
    if (busyId) return
    setBusyId(id)
    try { await api.post(`/memory/records/${id}/forget`); setItems(items.filter(i => i.id !== id)) }
    catch (_) { setErr('That memory could not be removed just now.') }
    setBusyId(null)
  }
  const correct = async (id) => {
    if (busyId || !draft.trim()) return
    setBusyId(id)
    try {
      const r = await api.post(`/memory/records/${id}/correct`, { text: draft.trim() })
      setItems(items.map(i => (i.id === id ? r.record : i)))
      setEditing(null); setDraft('')
    } catch (_) { setErr('That correction could not be saved just now.') }
    setBusyId(null)
  }
  const download = async () => {
    try {
      const data = await api.get('/memory/export')
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = `sovranova-memory-${new Date().toISOString().slice(0, 10)}.json`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 2000)
    } catch (_) { setErr('The download did not work just now.') }
  }
  const when = (r) => {
    const d = r.recordedAt ? new Date(r.recordedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
    return r.eventLabel ? `${d} · about ${r.eventLabel}` : d
  }

  return (
    <div style={{ marginTop:'1.5rem' }}>
      <p style={s.sectionLabel}>WHAT SOVRANOVA REMEMBERS</p>
      <p style={s.rowDesc}>
        Each memory shows where it came from and when. If something isn&rsquo;t right, tell it &mdash; the correction
        becomes what it remembers, and the old version is kept as history, not as truth. You can also remove a
        memory, or download everything.
      </p>
      <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.6rem' }}>
        <button style={sy.fileBtn} onClick={toggle}>{open ? 'Hide' : 'Show my memories'}</button>
        <button style={sy.fileBtn} onClick={download}>Download (JSON)</button>
      </div>
      {err && <p style={{ ...s.rowDesc, color:'#a03020', marginTop:'0.4rem' }}>{err}</p>}
      {open && (
        <div style={{ marginTop:'0.6rem' }}>
          {items === null && <p style={s.rowDesc}>Loading&hellip;</p>}
          {items && items.length === 0 && <p style={s.rowDesc}>Nothing remembered yet &mdash; memories are written when a session ends.</p>}
          {items && items.map(r => (
            <div key={r.id} style={sy.memCard}>
              <p style={sy.memMeta}>{when(r)} &middot; from {r.sourceLabel}{r.derived ? ' · SOVRANOVA\u2019s summary' : ''}{r.promoted ? ' · carried over from a couples session you both saved' : ''}{r.type === 'correction' ? ' · your correction' : ''}</p>
              <p style={sy.memText}>{r.content}</p>
              {r.userWords && r.type !== 'correction' && <p style={sy.memWords}>&ldquo;{r.userWords}&rdquo;</p>}
              {editing === r.id ? (
                <div>
                  <textarea style={{ ...sy.area, marginTop:'0.4rem' }} rows={3} value={draft} onChange={e => setDraft(e.target.value)} placeholder="What\u2019s the accurate version?" />
                  <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.4rem' }}>
                    <button style={sy.importBtn} onClick={() => correct(r.id)} disabled={busyId === r.id || !draft.trim()}>{busyId === r.id ? 'Saving\u2026' : 'Save correction'}</button>
                    <button style={sy.again} onClick={() => { setEditing(null); setDraft('') }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.4rem' }}>
                  {r.type !== 'correction' && <button style={sy.again} onClick={() => { setEditing(r.id); setDraft('') }}>That&rsquo;s not right</button>}
                  <button style={sy.again} onClick={() => forget(r.id)} disabled={busyId === r.id}>{busyId === r.id ? 'Removing\u2026' : 'Forget this'}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const sy = {
  memCard:   { background:'var(--og-n100, #f9f4ed)', border:'1.5px solid var(--og-n300, #dcd3c4)', borderRadius:'16px', padding:'0.7rem 0.9rem', marginTop:'0.5rem' },
  memMeta:   { fontSize:'0.7rem', color:'var(--og-n500, #a19786)', margin:'0 0 0.3rem', letterSpacing:'0.02em' },
  memText:   { fontSize:'0.84rem', color:'var(--og-n900, #2e2b25)', lineHeight:1.5, margin:0 },
  memWords:  { fontSize:'0.8rem', color:'var(--og-n700, #645c50)', fontStyle:'italic', margin:'0.3rem 0 0' },
  area:      { width:'100%', boxSizing:'border-box', background:'var(--og-n100, #f9f4ed)', border:'1.5px solid var(--og-n300, #dcd3c4)', borderRadius:'16px', padding:'0.75rem 0.9rem', fontSize:'0.88rem', color:'var(--og-n900, #2e2b25)', lineHeight:1.5, resize:'vertical', fontFamily:'inherit', marginTop:'0.6rem' },
  fileBtn:   { padding:'0.6rem 0.9rem', background:'var(--og-n100, #f9f4ed)', border:'1.5px solid var(--og-n300, #dcd3c4)', color:'var(--og-n700, #645c50)', borderRadius:'999px', fontSize:'0.8rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  importBtn: { flex:1, padding:'0.6rem 0.9rem', background:'var(--og-accent, #c67139)', border:'none', color:'var(--og-bg, #f5ead8)', borderRadius:'999px', fontSize:'0.84rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  learnedBox:{ background:'rgba(122,138,94,.12)', border:'1.5px solid rgba(122,138,94,.3)', borderRadius:'18px', padding:'0.9rem 1rem', marginTop:'0.6rem' },
  learnedText:{ fontSize:'0.86rem', color:'var(--og-n900, #2e2b25)', lineHeight:1.6, margin:0 },
  again:     { marginTop:'0.6rem', padding:'0.5rem 0.9rem', background:'transparent', border:'1.5px solid var(--og-n300, #dcd3c4)', color:'var(--og-n700, #645c50)', borderRadius:'999px', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
}

export function SettingsPanel({ onClose }) {
  const { settings, setSetting, resetSettings } = useSettings()
  const [pushBusy, setPushBusy] = useState(false)
  // Account deletion (App Store / Play requirement, 2026-08-13): two-step
  // confirm, then the backend removes the auth user and all their data.
  const [deleteStep, setDeleteStep] = useState(0)   // 0 idle · 1 confirming
  const [deleting, setDeleting]     = useState(false)
  const [deleteErr, setDeleteErr]   = useState(null)
  // Who am I? Testers running multiple accounts had no way to tell which one
  // was signed in (Wave 1, 20 Aug) — show the account email in ACCOUNT.
  const [accountEmail, setAccountEmail] = useState(null)
  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data }) => setAccountEmail(data?.session?.user?.email || null))
      .catch(() => {})
  }, [])

  const handleDeleteAccount = async () => {
    if (deleting) return
    setDeleting(true); setDeleteErr(null)
    try {
      await api.del('/auth/account')
      try { localStorage.clear() } catch (_) {}
      try { await supabase.auth.signOut() } catch (_) {}
      window.location.href = '/'
    } catch (e) {
      setDeleteErr('Deletion failed — please try again, or email support.')
      setDeleting(false)
    }
  }
  // Hidden entirely — not disabled, not shown-and-greyed — whenever
  // survivor-safe/disguise mode is active. See hooks/useDisguiseMode.js for
  // why this checks a not-yet-wired-up flag: no such mode exists anywhere in
  // this codebase today (grepped, documented there), so this always renders
  // the toggle for now; it's forward-compatible for if/when one ships.
  const showNotificationsToggle = !isDisguiseModeActive()

  const handlePushToggle = async (turnOn) => {
    if (pushBusy) return
    setPushBusy(true)
    if (turnOn) {
      const result = await subscribeToPush()
      if (result.ok) setSetting('pushNotifications', true)
      // Denied permission, unsupported browser, or backend not configured —
      // leave the toggle off and say nothing alarming; this is a quiet,
      // optional feature, never a blocking error state.
    } else {
      await unsubscribeFromPush()
      setSetting('pushNotifications', false)
    }
    setPushBusy(false)
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>
        <div style={s.head}>
          <h2 style={s.title}>Settings</h2>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.body}>
          <p style={s.sectionLabel}>VOICE &amp; INTERACTION</p>

          <Row
            title="Text-only mode"
            desc="Accessibility: turn off all voice. Read and type only."
            on={settings.textOnly}
            onChange={v => setSetting('textOnly', v)}
          />
          <Row
            title="AI voice"
            desc="SOVRANOVA speaks responses aloud."
            on={settings.voiceEnabled}
            onChange={v => setSetting('voiceEnabled', v)}
            disabled={settings.textOnly}
          />
          <Row
            title="Auto-play voice"
            desc="Voice begins automatically where your browser allows."
            on={settings.autoplayVoice}
            onChange={v => setSetting('autoplayVoice', v)}
            disabled={settings.textOnly}
          />
          <Row
            title="Echo-safe mode"
            desc="Only if her own voice triggers replies on loud speakerphone. Turns off talking over her to interrupt."
            on={settings.echoSafeMode}
            onChange={v => setSetting('echoSafeMode', v)}
            disabled={settings.textOnly}
          />
          <Row
            title="Dashboard companion"
            desc="Show the listening companion when you open the app."
            on={settings.companionOnDashboard}
            onChange={v => setSetting('companionOnDashboard', v)}
            disabled={settings.textOnly}
          />
          {showNotificationsToggle && (
            <Row
              title="Notifications"
              desc="A gentle reminder on this device when something's waiting for you. Off by default."
              on={settings.pushNotifications}
              onChange={handlePushToggle}
              disabled={pushBusy}
            />
          )}

          <VoicePicker />
          {/* Conversation voice — hidden for beta wave 1. The list is the raw
              Hume Voice Library (~120 entries: "Dungeon Master", "Pirate
              Captain", "Comical Vampire" …), which is not a set of voices
              SOVRANOVA has chosen for a wellbeing companion. Hiding the picker
              leaves live voice on the app default and changes nothing
              server-side; a curated allowlist can restore it later. */}
          {FEATURE_HUME_VOICE_PICKER && <ConversationVoicePicker />}
          <RegionPicker />

          <button style={s.reset} onClick={resetSettings}>Reset to defaults</button>

          <ShareYourStory />
          <WhatIRemember />

          <p style={{ ...s.sectionLabel, marginTop:'1.5rem' }}>ACCOUNT</p>
          {accountEmail && (
            <p style={{ fontSize:'0.84rem', color:'var(--og-n700, #645c50)', margin:'0 0 0.6rem' }}>
              Signed in as <strong style={{ wordBreak:'break-all' }}>{accountEmail}</strong>
            </p>
          )}
          {deleteStep === 0 ? (
            <button style={s.dangerBtn} onClick={() => setDeleteStep(1)}>Delete my account…</button>
          ) : (
            <div style={s.dangerBox}>
              <p style={s.dangerText}>
                This permanently deletes your account and everything Sovranova remembers about you —
                sessions, journal, check-ins, progress, memory. There is no undo. One exception: if a safety
                check ever flagged a crisis, that safety record is kept for audit with your account link and
                session link removed — it can no longer be tied to you.
              </p>
              {deleteErr && <p style={{ ...s.dangerText, fontWeight:700 }}>{deleteErr}</p>}
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.6rem' }}>
                <button style={s.dangerConfirm} onClick={handleDeleteAccount} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Yes, delete everything'}
                </button>
                <button style={s.dangerCancel} onClick={() => { setDeleteStep(0); setDeleteErr(null) }} disabled={deleting}>
                  Keep my account
                </button>
              </div>
            </div>
          )}
          <p style={s.footNote}>
            You can also request deletion any time from sovranova.app/privacy.html.
          </p>

          <p style={s.footNote}>
            SOVRANOVA is voice-first by default. Text-only mode is always available for accessibility — your choice is remembered on this device.
          </p>
        </div>
      </div>
    </div>
  )
}

const s = {
  // Organic restyle (Board S1, 2026-08-13): warm cream drawer. Values only.
  overlay:  { position:'fixed', inset:0, background:'rgba(32,30,29,0.45)', display:'flex', justifyContent:'flex-end', zIndex:2000 },
  panel:    { width:'100%', maxWidth:380, height:'100%', background:'var(--og-bg, #f5ead8)', display:'flex', flexDirection:'column', boxShadow:'-8px 0 40px rgba(46,43,37,0.3)' },
  head:     { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.25rem 1.5rem', borderBottom:'1.5px solid var(--og-n200, #eee7db)' },
  title:    { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.3rem', fontWeight:400, color:'var(--og-a900, #402310)', margin:0 },
  closeBtn: { background:'transparent', border:'none', color:'var(--og-n500, #a19786)', fontSize:'1.6rem', cursor:'pointer', lineHeight:1 },
  body:     { flex:1, overflowY:'auto', padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.5rem' },
  sectionLabel: { fontSize:'0.66rem', fontWeight:700, letterSpacing:'0.12em', color:'var(--og-a700, #8c491a)', marginBottom:'0.5rem', textTransform:'uppercase' },
  row:      { display:'flex', alignItems:'center', gap:'1rem', padding:'0.9rem 0', borderBottom:'1.5px solid var(--og-n200, #eee7db)' },
  rowTitle: { fontSize:'0.95rem', fontWeight:600, color:'var(--og-n900, #2e2b25)', margin:0 },
  rowDesc:  { fontSize:'0.78rem', color:'var(--og-n700, #645c50)', margin:'0.2rem 0 0', lineHeight:1.5 },
  reset:    { marginTop:'1.25rem', padding:'0.75rem', background:'var(--og-n100, #f9f4ed)', border:'none', color:'var(--og-n700, #645c50)', borderRadius:'999px', fontSize:'0.85rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  footNote: { fontSize:'0.72rem', color:'var(--og-n500, #a19786)', lineHeight:1.6, marginTop:'1rem' },
  dangerBtn: { padding:'0.75rem', background:'transparent', border:'1.5px solid rgba(192,57,43,.35)', color:'#a03020', borderRadius:'999px', fontSize:'0.85rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  dangerBox: { background:'rgba(192,57,43,.06)', border:'1.5px solid rgba(192,57,43,.25)', borderRadius:'18px', padding:'0.9rem 1rem' },
  dangerText: { fontSize:'0.8rem', color:'#7a2418', lineHeight:1.55, margin:0 },
  dangerConfirm: { flex:1, padding:'0.65rem', background:'#a03020', border:'none', color:'#fff', borderRadius:'999px', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
  dangerCancel: { flex:1, padding:'0.65rem', background:'var(--og-n100, #f9f4ed)', border:'none', color:'var(--og-n700, #645c50)', borderRadius:'999px', fontSize:'0.82rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
}
